import React, { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import Map from "./Dashboard/Map";

const MapPopup = ({
    isVisible,
    onClose,
    location = null,
    locations = [],
    customIcon = null,
    title = "Localisation",
}) => {
    const [isFullscreen, setIsFullscreen] = useState(false);
    const [resizeTick, setResizeTick] = useState(0);
    const overlayRef = useRef(null);
    const dialogRef = useRef(null);
    const previouslyFocusedRef = useRef(null);
    const titleId = useRef(`mappopup-title-${Math.random().toString(36).slice(2)}`).current;

    useEffect(() => {
        if (!isVisible) return;
        const onEsc = (e) => e.key === "Escape" && onClose?.();
        document.addEventListener("keydown", onEsc);
        return () => document.removeEventListener("keydown", onEsc);
    }, [isVisible, onClose]);

    useEffect(() => {
        if (!isVisible) return;
        const prevOverflow = document.body.style.overflow;
        document.body.style.overflow = "hidden";
        previouslyFocusedRef.current = document.activeElement;

        const t = setTimeout(() => dialogRef.current?.focus(), 0);
        return () => {
            document.body.style.overflow = prevOverflow;
            clearTimeout(t);
            previouslyFocusedRef.current?.focus?.();
        };
    }, [isVisible]);

    // ---- Focus trap
    useEffect(() => {
        if (!isVisible) return;
        const handleKeyDown = (e) => {
            if (e.key !== "Tab") return;
            const focusable = dialogRef.current?.querySelectorAll(
                'a[href], button, textarea, input, select, [tabindex]:not([tabindex="-1"])'
            );
            if (!focusable?.length) return;
            const first = focusable[0];
            const last = focusable[focusable.length - 1];
            if (e.shiftKey && document.activeElement === first) {
                e.preventDefault(); last.focus();
            } else if (!e.shiftKey && document.activeElement === last) {
                e.preventDefault(); first.focus();
            }
        };
        dialogRef.current?.addEventListener("keydown", handleKeyDown);
        return () => dialogRef.current?.removeEventListener("keydown", handleKeyDown);
    }, [isVisible]);

    const locationsToShow = useMemo(() => {
        if (Array.isArray(locations) && locations.length) return locations;
        if (location) return [location];
        return [];
    }, [location, locations]);

    // ---- Fermer uniquement si mousedown + mouseup sur overlay, sans drag
    const downOnOverlayRef = useRef(false);
    const movedRef = useRef(false);

    const handlePointerDown = (e) => {
        downOnOverlayRef.current = (e.target === overlayRef.current);
        movedRef.current = false;
    };
    const handlePointerMove = () => {
        if (downOnOverlayRef.current) movedRef.current = true;
    };
    const handlePointerUp = (e) => {
        const endedOnOverlay = (e.target === overlayRef.current);
        if (downOnOverlayRef.current && endedOnOverlay && !movedRef.current) {
            onClose?.();
        }
        downOnOverlayRef.current = false;
        movedRef.current = false;
    };

    useEffect(() => {
        if (!isVisible) return;
        setResizeTick((t) => t + 1);
    }, [isVisible]);

    useEffect(() => {
        if (!isVisible) return;
        setResizeTick((t) => t + 1);
    }, [isFullscreen, isVisible]);

    useEffect(() => {
        if (!isVisible) return;
        const el = dialogRef.current;
        if (!el) return;
        const onEnd = (e) => {
            if (e.target === el) {
                setResizeTick((t) => t + 1);
            }
        };
        el.addEventListener("transitionend", onEnd);
        return () => el.removeEventListener("transitionend", onEnd);
    }, [isVisible]);

    if (!isVisible) return null;

    const content = (
        <div
            ref={overlayRef}
            className="z-[9999] fixed inset-0 flex justify-center items-center bg-black/25 opacity-100 transition-opacity duration-300"
            aria-hidden="true"
            onPointerDown={handlePointerDown}
            onPointerMove={handlePointerMove}
            onPointerUp={handlePointerUp}
        >
            <div
                ref={dialogRef}
                role="dialog"
                aria-modal="true"
                aria-labelledby={titleId}
                tabIndex={-1}
                className={`relative bg-white overflow-hidden flex flex-col transition-all duration-500 ease-in-out outline-none
        ${isFullscreen ? "w-[95vw] h-[95vh] p-4 rounded-none" : "w-[64rem] h-[80vh] p-4 rounded-xl"}`}
            >
                <div className="flex items-center gap-3 mb-3">
                    <div className="inline-flex items-center gap-2 bg-orange-600 px-3 py-2 rounded-lg text-white">
                        <i className="fa-solid fa-map-location-dot" />
                        <span id={titleId} className="font-semibold text-sm">{title}</span>
                    </div>
                    <div className="flex items-center gap-2 ml-auto">
                        <button
                            type="button"
                            onClick={() => setIsFullscreen((v) => !v)}
                            className="group flex items-center bg-orange-600 hover:bg-orange-500 px-3 py-2 rounded-lg text-white text-sm transition"
                            title={isFullscreen ? "Quitter le plein écran" : "Plein écran"}
                        >
                            <i className={`fa-solid ${isFullscreen ? "fa-minimize" : "fa-maximize"}`} />
                            <span className="hidden sm:inline ml-2">{isFullscreen ? "Quitter" : "Plein écran"}</span>
                        </button>
                        <button
                            type="button"
                            onClick={onClose}
                            className="flex items-center bg-red-500 hover:bg-red-400 px-3 py-2 rounded-lg text-white text-sm transition"
                            title="Fermer"
                        >
                            <i className="fa-solid fa-xmark fa-lg" />
                            <span className="hidden sm:inline ml-2">Fermer</span>
                        </button>
                    </div>
                </div>

                <div className="relative flex-1 rounded w-full">
                    <div className="absolute inset-0">
                        <Map
                            locations={locationsToShow}
                            customIcon={customIcon || null}
                            resizeSignal={resizeTick}
                        />
                    </div>
                </div>
            </div>
        </div>
    );

    return createPortal(content, document.body);
};

export default MapPopup;
